import { useState, useRef } from 'react';
import { Upload, X, FileText, Image, File, CheckCircle, AlertCircle } from 'lucide-react';
import { api } from '../../utils/api.js';
import { compressFile } from '../../utils/compress.js';
import { formatBytes } from '../../utils/formatters.js';
import { ACCEPTED_FILE_TYPES } from '../../utils/constants.js';

function FileIcon({ mimeType }) {
  if (mimeType?.startsWith('image/')) return <Image className="w-4 h-4 text-blue-500" />;
  if (mimeType === 'application/pdf') return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-gray-400" />;
}

export default function FileUpload({ gatePassId, deliveryNoteId, onUploaded, existingFiles = [], disabled }) {
  const [uploading, setUploading] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const fileArray = Array.from(files);
    for (const rawFile of fileArray) {
      if (!ACCEPTED_FILE_TYPES.includes(rawFile.type)) {
        alert(`"${rawFile.name}" is not an accepted file type.`);
        continue;
      }

      const uploadId = `${Date.now()}-${Math.random()}`;
      setUploading(prev => [...prev, {
        id: uploadId, name: rawFile.name, status: 'compressing', progress: 0,
        originalSize: rawFile.size, compressedSize: null,
      }]);

      try {
        // 1. Compress
        const { file, originalSize, compressedSize } = await compressFile(rawFile, (p) => {
          setUploading(prev => prev.map(u => u.id === uploadId ? { ...u, progress: p * 0.3 } : u));
        });
        setUploading(prev => prev.map(u => u.id === uploadId ? {
          ...u, status: 'uploading', compressedSize, progress: 30,
        } : u));

        // 2. Get pre-signed URL
        const { data: urlData } = await api.post('/files/presigned-url', {
          filename: file.name, mimeType: file.type, sizeBytes: file.size,
          gatePassId, deliveryNoteId,
        });

        // 3. Upload directly to R2
        await fetch(urlData.data.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });

        setUploading(prev => prev.map(u => u.id === uploadId ? { ...u, progress: 90 } : u));

        // 4. Confirm upload
        const { data: fileData } = await api.post('/files/confirm', {
          r2Key: urlData.data.r2Key,
          originalName: rawFile.name,
          mimeType: file.type,
          sizeBytes: file.size,
          gatePassId, deliveryNoteId,
        });

        setUploading(prev => prev.map(u => u.id === uploadId ? { ...u, status: 'done', progress: 100 } : u));
        onUploaded?.(fileData.data);

        // Auto-remove from uploading list after 2s
        setTimeout(() => setUploading(prev => prev.filter(u => u.id !== uploadId)), 2000);
      } catch (err) {
        setUploading(prev => prev.map(u => u.id === uploadId ? {
          ...u, status: 'error', error: err.response?.data?.message || err.message,
        } : u));
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      {!disabled && (
        <div
          className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
            dragOver ? 'border-teal-400 bg-teal-50' : 'border-gray-300 hover:border-teal-300 hover:bg-gray-50'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 font-medium">Drag & drop files here, or click to browse</p>
          <p className="text-xs text-gray-400 mt-1">JPEG, PNG, HEIC, PDF, DOCX, XLSX — images auto-compressed to max 1500px</p>
          <input ref={inputRef} type="file" multiple hidden
            accept={ACCEPTED_FILE_TYPES.join(',')}
            onChange={(e) => handleFiles(e.target.files)} />
        </div>
      )}

      {/* Upload progress */}
      {uploading.map(u => (
        <div key={u.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
          <FileIcon mimeType={u.mimeType} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{u.name}</p>
            <div className="flex items-center gap-2 mt-0.5">
              {u.status === 'done' ? (
                <span className="text-xs text-green-600">
                  Done · saved {formatBytes(u.originalSize - u.compressedSize)} via compression
                </span>
              ) : u.status === 'error' ? (
                <span className="text-xs text-red-600">{u.error}</span>
              ) : (
                <>
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                    <div className="bg-teal-500 h-1.5 rounded-full transition-all" style={{ width: `${u.progress}%` }} />
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {u.status === 'compressing' ? 'Compressing…' : `${u.progress}%`}
                  </span>
                </>
              )}
            </div>
          </div>
          {u.status === 'done' && <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />}
          {u.status === 'error' && <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />}
        </div>
      ))}

      {/* Existing files */}
      {existingFiles.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Attached Files</p>
          {existingFiles.map(f => (
            <div key={f.id} className="flex items-center gap-3 p-2.5 bg-white border rounded-lg">
              <FileIcon mimeType={f.mimeType} />
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{f.originalName}</p>
                <p className="text-xs text-gray-400">{formatBytes(f.sizeBytes)}</p>
              </div>
              <button
                onClick={async () => {
                  const { data } = await api.get(`/files/${f.id}/download`);
                  window.open(data.data.url, '_blank');
                }}
                className="text-xs text-teal-600 hover:underline whitespace-nowrap"
              >
                Download
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
